import {
  DirectionsRenderer,
  OverlayView,
} from '@react-google-maps/api';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CourierMarker from '../CourierMarker';
import { CourierReturnStatusType, CourierVehicleType, ICourier } from '../../context/couriers';
import useTheme from '@material-ui/core/styles/useTheme';
import { IUser } from '@src/shared/context/current-user';
import { useOrdersState } from '../../context/orders';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import DatadogService from '@shared/services/datadog';
import OrderMarker from '../OrderMarker';
interface IGMAPWaypoints {
  location: GMAPCoordinates;
  stopover: boolean;
}

interface IRouteStates {
  destination: GMAPCoordinates;
  waypoints: IGMAPWaypoints[];
  origin: GMAPCoordinates;
}

type OmitedObject = Omit<Coordinates, keyof Coordinates>;

const CourierRoute: FC<ICourier> = ({
  orderPool,
  firstName,
  lastName,
  location,
  id,
  accentColor,
  returnBack,
  backBy,
  vehicleType,
  isGuest
}) => {
  const waypoints = orderPool?.route.waypoints;
  const theme = useTheme();
  const { ordersById } = useOrdersState();
  const offsetHandler = useCallback(
    (_, offsetHeight) => ({
      x: -11,
      y: 11 - offsetHeight,
    }),
    []
  );
  const { restaurant } = useKeycloakUserState();
  const lastCourierState = useRef({lat: 0, lng: 0, returnStatus : null})


  const directionPolylineOptions = {
    strokeColor: theme.palette.jet?.containerDark,
    strokeOpacity: 1,
    strokeWeight: 4,
    fillColor: theme.palette.jet?.containerDark,
    fillOpacity: 1,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    radius: 30000,
    zIndex: 1,
  };

  const getTravelMode = useMemo(() => {
    switch (vehicleType) {
      case CourierVehicleType.Car:
        return 'DRIVING';
      default:
        return 'BICYCLING';
    }
  }, [vehicleType]);

  const [routeState, setRouteState] = useState<IRouteStates>({
    destination: null,
    waypoints: [],
    origin: null,
  });

  const callDirectionsService = (requestOptions : google.maps.DirectionsRequest) => {
    const DirectionsService = new google.maps.DirectionsService();
    DirectionsService.route(requestOptions, (result, status) => {
      switch(status) {
        case google.maps.DirectionsStatus.OK:
          setDirectionResponse(result as any);
        break;
        case google.maps.DirectionsStatus.OVER_QUERY_LIMIT:
          DatadogService.createError('The webpage has gone over the requests limit in too short a period of time', { type:'Google Map Direction Service', message: ` ${waypoints?.length} of waypoints was set`});
          break;
        case google.maps.DirectionsStatus.MAX_WAYPOINTS_EXCEEDED:
          DatadogService.createError('Max Waypoints Exceeded', { type:'Google Map Direction Service',message: ` ${waypoints?.length} of waypoints was set`});
        break;
        case google.maps.DirectionsStatus.NOT_FOUND:
          break;
        case google.maps.DirectionsStatus.UNKNOWN_ERROR:
          DatadogService.createError('A directions request could not be processed due to a server error.',{type:'Google Map Direction Service'})
        break;
        case google.maps.DirectionsStatus.ZERO_RESULTS:
          DatadogService.createError('No route could be found between the origin and destination.', {type:'Google Map Direction Service', message: ` ${waypoints?.length} of waypoints was set`});
          break;
        case google.maps.DirectionsStatus.INVALID_REQUEST:
          DatadogService.createError('The DirectionsRequest provided was invalid.',{type:'Google Map Direction Service'})
          break;
      }
    });
  }
  

  useEffect(()=> {

    const { lat : oldLat , lng : oldLng, returnStatus } = lastCourierState.current

    if (location?.latitude === oldLat && location?.longitude === oldLng && returnBack?.status === returnStatus) {
      return
    }

    if (returnBack?.status === CourierReturnStatusType.Returning && location) {
      callDirectionsService({
        origin: {lat: location?.latitude, lng: location?.longitude},
        destination:{ lat: (restaurant as IUser)?.location?.lat, lng: (restaurant as IUser)?.location?.lng,},
        travelMode:  getTravelMode as any,
      })

      if (location?.latitude && location?.longitude) {
        lastCourierState.current.lat = location?.latitude
        lastCourierState.current.lng = location?.longitude
        lastCourierState.current.returnStatus = returnBack?.status as any
      }
      
    }

    if (waypoints?.length && location) {
      callDirectionsService({
        destination: routeState.destination as any,
        origin: {lat: location.latitude, lng: location.longitude},
        waypoints: [...routeState.waypoints] as any,
        travelMode: getTravelMode as any,
      })
      
      if (routeState.destination !== null) {
        lastCourierState.current.lat = location.latitude
        lastCourierState.current.lng = location.longitude
        lastCourierState.current.returnStatus = returnBack?.status as any
      }

    }
  }, [
      location?.latitude,
      location?.longitude,
      backBy?.eta,
      routeState.origin?.lat,
      routeState.origin?.lng,
      getTravelMode,
      waypoints,
      returnBack
    ])

    

  const courier : ICourier = {  
    id,
    firstName,
    lastName,
    vehicleType,
    location : location as Coordinates,
    accentColor,
    returnBack,
    isGuest,
    backBy,
    orderPool,
    }
 

  const [directionResponse, setDirectionResponse] =
    useState<IRouteStates | null>(null);

  useEffect(() => {
    const courierOrigin: GMAPCoordinates = {
      lat: location?.latitude,
      lng: location?.longitude,
    };

    if (waypoints && orderPool && orderPool.nextOrderReference !== null) {
      const courierRoutes: Coordinates[] = waypoints
        .filter((wy) => wy.isDelivered === false)
        .map((item) => item.location);

      const resettleWaypoints: IGMAPWaypoints[] = courierRoutes.reduce(
        (
          gMapWayPoints: IGMAPWaypoints[],
          locationValue: Coordinates,
          index
        ) => {
          gMapWayPoints[index] = {
            location: renameLocationObject(locationValue),
            stopover: true,
          };
          return gMapWayPoints;
        },
        []
      );

      setDirections(
        courierOrigin,
        renameLocationObject(
          courierRoutes[courierRoutes.length - 1] as Coordinates
        ),
        resettleWaypoints
      );
    }
    if (
      returnBack &&
      returnBack.status === CourierReturnStatusType.Returning &&
      orderPool &&
      orderPool.nextOrderReference === null
    ) {
      setDirections(
        courierOrigin,
        {
          lat: (restaurant as IUser)?.location?.lat,
          lng: (restaurant as IUser)?.location?.lng,
        },
        []
      );
    }
  }, [location, waypoints?.length, orderPool?.nextOrderReference, vehicleType]);

  //TODO: Make this as global utility
  const renameLocationObject = ({
    latitude,
    longitude,
  }: Coordinates): OmitedObject & GMAPCoordinates => ({
    lat: latitude,
    lng: longitude,
  });



  const setDirections = (
    origin: GMAPCoordinates,
    destination: GMAPCoordinates,
    waypoints: IGMAPWaypoints[] | []
  ) => {
    setRouteState({
      ...routeState,
      origin,
      destination,
      waypoints: waypoints,
    });
  };



  return (
    <>
      <CourierMarker
        firstName={firstName}
        lastName={lastName}
        location={location}
        id={id}
        accentColor={accentColor}
        orderPool={orderPool}
        backBy={backBy}
        returnBack={returnBack}
        vehicleType={vehicleType}
        isGuest={isGuest}
      />

      {directionResponse && (
                  <DirectionsRenderer
                    options={{
                      directions: directionResponse as any,
                      suppressBicyclingLayer: true,
                      suppressMarkers: true,
                      polylineOptions: directionPolylineOptions,
                    }}                   
                    onUnmount={() => {
                      setDirectionResponse(null);
                    }}
                  />
                )}


      {waypoints && location ? (
        <>
          {Object.keys(waypoints).map((_, index) => {
            return (
              <OverlayView
                key={index}
                position={{
                  lat: waypoints[index]?.location?.latitude,
                  lng: waypoints[index]?.location?.longitude,
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={offsetHandler}
              >
                
               {<>
                  <OrderMarker 
                  {...(ordersById[waypoints[index]?.order?.uuid] || {})}
                  courier={courier} sequenceNumber={index + 1}/>
                </>}
              </OverlayView>
            );
          })}
        </>
      ) : null}
    </>
  );
};

export default memo(CourierRoute);
